"use client"

import { useState } from "react"
import { useNavigate, Link } from "react-router-dom"
import { useSelector } from "react-redux"
import Countdown from "react-countdown"
import { Mail, Lock, KeyRound, ArrowLeft, RefreshCw, Leaf } from "lucide-react"

// Import your existing utilities and components
import APIRequests from "../../../utility/APIRequests"
import { notify } from "../../../utility/Helper1"
import { allRemoteSettingsSelect } from "../../../redux/selectors/index"
import ReCAPTCHA from "../../../utility/customComponents/ReCAPTCHA";
import AuthHolder from "./AuthHolder";

const ResetPassword = () => {
    const [emailInput, setEmailInput] = useState("")
    const [newPassword, setNewPassword] = useState("")
    const [confirmNewPassword, setConfirmNewPassword] = useState("")
    const [emailCode, setEmailCode] = useState("")
    const [requestOTPRetObj, setRequestOTPRetObj] = useState({})
    const [uiLevel, setUiLevel] = useState(0)
    const [fullPageLoading, setFullPageLoading] = useState(false)
    const [loadingText, setLoadingText] = useState("")
    const [loading, changeLoading] = useState(false)
    const [captcha, setCaptcha] = useState(null)

    const navigate = useNavigate()
    const site_settings = useSelector(allRemoteSettingsSelect)

    const captchaHasChanged = (data) => {
        setCaptcha(data)
    }

    const requestOTP = async (e) => {
        e.preventDefault()

        if (loading) {
            return
        }

        setFullPageLoading(true)
        changeLoading(true)

        const loadApi = new APIRequests()
        const getPost = await loadApi.runPost("account/reset_password_pin", { email: emailInput })

        if (getPost?.code == 1) {
            setUiLevel(1)
            setRequestOTPRetObj(getPost?.data)
        }

        changeLoading(false)
        notify(getPost?.msg, getPost?.code)
        setFullPageLoading(false)
    }

    const changePassword = async (e) => {
        e.preventDefault()

        if (loading) {
            return
        }

        if (!captcha && Number(site_settings?.site_settings?.use_google_reCaptcha) == 1) {
            notify("Invalid captcha response", 0)
            return
        }

        if (newPassword != confirmNewPassword || newPassword == "") {
            notify("Passwords must match", 0)
            return
        }

        changeLoading(true)
        setLoadingText("Please wait...")
        setFullPageLoading(true)

        const loadApi = new APIRequests()
        const getPost = await loadApi.runPost("account/reset_password", {
            captcha,
            email: emailInput,
            password: newPassword,
            passwordResetCode: emailCode,
        })

        if (getPost?.code == 1) {
            setUiLevel(0)
            notify("Password reset successful. You are being redirected to login page", 1)

            setTimeout(() => {
                navigate("/signin", { replace: true })
            }, 3000)
        } else {
            notify(getPost?.msg ?? "error", getPost?.code ?? 0)
        }

        changeLoading(false)
        setLoadingText("")
        setFullPageLoading(false)
    }

    const handleReload = () => {
        window.location.reload()
    }

    return (<AuthHolder>

        <div className="space-y-1 p-6 pb-4">
            <h2 className="text-2xl font-bold text-center text-green-800">
                {uiLevel === 0 ? "Reset Password" : "Update Your Password"}
            </h2>
            <p className="text-center text-green-600">
                {uiLevel === 0
                    ? "Account email address"
                    : "Update Account Password"}
            </p>
        </div>

        <div className="p-6 pt-2">
            {uiLevel == 0 && (
                <form onSubmit={requestOTP} className="space-y-4">
                    <div className="space-y-2">
                        <label htmlFor="email-id" className="block text-green-700 font-medium">
                            Email Address
                        </label>
                        <div className="relative">
                            <Mail className="absolute left-3 top-2.5 h-5 w-5 text-green-500" />
                            <input
                                id="email-id"
                                type="email"
                                placeholder="hello@example.com"
                                value={emailInput}
                                onChange={(e) => setEmailInput(e.target.value)}
                                required
                                className="w-full pl-10 pr-3 py-2 border border-green-200 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="w-full mt-4 bg-green-600 hover:bg-green-700 text-white font-medium py-2.5 px-4 rounded-md transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed"
                        disabled={loading}
                    >
                        {loading ? (
                            <div className="flex items-center justify-center">
                                <svg
                                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    ></circle>
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                </svg>
                                Sending Reset Code...
                            </div>
                        ) : (
                            "Send Reset Code"
                        )}
                    </button>
                </form>
            )}

            {uiLevel == 1 && (
                <form onSubmit={changePassword} className="space-y-4">
                    <div className="space-y-2">
                        <label htmlFor="new-password-1" className="block text-green-700 font-medium">
                            New Password
                        </label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-2.5 h-5 w-5 text-green-500" />
                            <input
                                id="new-password-1"
                                type="password"
                                placeholder="Enter new password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                required
                                className="w-full pl-10 pr-3 py-2 border border-green-200 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label htmlFor="new-password-2" className="block text-green-700 font-medium">
                            Confirm Password
                        </label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-2.5 h-5 w-5 text-green-500" />
                            <input
                                id="new-password-2"
                                type="password"
                                placeholder="Confirm your password"
                                value={confirmNewPassword}
                                onChange={(e) => setConfirmNewPassword(e.target.value)}
                                required
                                className="w-full pl-10 pr-3 py-2 border border-green-200 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label htmlFor="new-password-3" className="block text-green-700 font-medium">
                            Reset Code
                        </label>
                        <div className="relative">
                            <KeyRound className="absolute left-3 top-2.5 h-5 w-5 text-green-500" />
                            <input
                                id="new-password-3"
                                type="text"
                                placeholder="Enter code from email"
                                value={emailCode}
                                onChange={(e) => setEmailCode(e.target.value)}
                                required
                                className="w-full pl-10 pr-3 py-2 border border-green-200 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                            />
                        </div>
                    </div>

                    <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-md">
                        <div className="flex items-center">
                            <div className="mr-2 text-green-600">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-5 w-5"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>
                            <div className="text-sm text-green-700">
                                <Countdown
                                    zeroPadTime={2}
                                    key={new Date(requestOTPRetObj?.passwordResetCodeTimer)}
                                    date={new Date(requestOTPRetObj?.passwordResetCodeTimer)}
                                    renderer={({ hours, minutes, seconds, completed }) => {
                                        if (completed) {
                                            return (
                                                <span className="flex items-center">
                                                    Code expired.{" "}
                                                    <button
                                                        onClick={handleReload}
                                                        className="ml-1 text-green-600 hover:text-green-800 font-medium flex items-center"
                                                    >
                                                        <RefreshCw className="h-3 w-3 mr-1" /> Try again
                                                    </button>
                                                </span>
                                            )
                                        } else {
                                            return (
                                                <span>
                                                    Code expires in{" "}
                                                    <span className="font-medium">
                                                        {minutes}:{seconds < 10 ? `0${seconds}` : seconds}
                                                    </span>
                                                </span>
                                            )
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="mt-4">
                        <div className="w-full">
                            <ReCAPTCHA onChange={captchaHasChanged} />
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="w-full mt-4 bg-green-600 hover:bg-green-700 text-white font-medium py-2.5 px-4 rounded-md transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed"
                        disabled={loading}
                    >
                        {loading ? (
                            <div className="flex items-center justify-center">
                                <svg
                                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    ></circle>
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                </svg>
                                Updating Password...
                            </div>
                        ) : (
                            "Update Password"
                        )}
                    </button>
                </form>
            )}
        </div>

        <div className="px-6 pb-6 pt-0">
            <div className="relative w-full my-4">
                <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-green-200" />
                </div>
                <div className="relative flex justify-center text-xs">
                    <span className="bg-white px-2 text-green-600">or</span>
                </div>
            </div>

            <p className="text-center text-sm text-green-700">
                Remember your password?{" "}
                <Link
                    to="/signin"
                    className="font-medium text-green-600 hover:text-green-800 hover:underline transition-colors"
                >
                    Sign in
                </Link>
            </p>
        </div>

    </AuthHolder>
    )
}

export default ResetPassword
